/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type SkeletonOwnProps, Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { BrowserFrame } from '../CKEditor5Iframe/BrowserFrame/BrowserFrame';
import { useDimensions } from '../../hooks/useDimensions/useDimensions';
import { PADDING_BOTTOM } from '../../constants/mockBrowser';

const MOCK_BROWSER_PADDING_TOP = 44;
// 20 is padding of an element above.
const EDITOR_PLACEHOLDER_PADDING_BOTTOM = PADDING_BOTTOM - MOCK_BROWSER_PADDING_TOP + 10;

export function EditorPreviewPlaceholder(): ReactNode {
	const { height: windowHeight } = useDimensions( document.body );

	const skeletonProps: SkeletonOwnProps = {
		sx: { opacity: '0.3' },
		animation: false,
		variant: 'rounded'
	};

	return (
		<div>
			<Alert
				sx={ {
					display: 'var(--low-resolution-warning)',
					'& .MuiAlert-icon > svg': {
						width: '16.77px' // This makes the <svg> exactly 14px.
					}
				} }
			>
				<Typography color='text.primary' fontWeight='400' fontSize='14px'>
					This resolution is too low to utilize all features. Please switch to a higher resolution for the best experience.
				</Typography>
			</Alert>
			<BrowserFrame style={ { marginTop: MOCK_BROWSER_PADDING_TOP + 'px', minWidth: '300px' } }>
				<Box
					sx={ {
						height: windowHeight - EDITOR_PLACEHOLDER_PADDING_BOTTOM + 'px',
						minHeight: '100px',
						maxWidth: '100%',
						display: 'flex',
						justifyContent: 'center',
						overflowY: 'auto'
					} }
					data-testid={ 'editor-preview-placeholder' }
				>
					<Stack sx={ { marginTop: '32px', marginBottom: '32px' } }>
						<Box sx={ {
							width: getPlaceholderWidth( 1 ),
							height: ' 32px',
							background: 'white',
							borderTopRightRadius: '2px',
							borderTopLeftRadius: '2px'
						} }
						/>
						<Box sx={ {
							width: getPlaceholderWidth( 1 ),
							height: ' 850px',
							background: 'white',
							borderBottomLeftRadius: '2px',
							borderBottomRightRadius: '2px',
							borderTop: 'none',
							position: 'relative'
						} }
						>
							<Stack sx={ {
								position: 'absolute',
								top: '100px',
								left: 0,
								right: 0,
								zIndex: 1
							} }
							>
								<EditorPreviewDescription/>
							</Stack>
							<Stack sx={ { marginTop: '32px', marginLeft: '32px', gap: '30px' } }>
								<Skeleton { ...skeletonProps } width={ 310 } height={ 40 }/>
								<Skeleton { ...skeletonProps } width={ getPlaceholderWidth( 0.9 ) } component='div'>
									<EditorPreviewDescription/>
								</Skeleton>
								<Skeleton { ...skeletonProps } width={ 310 } height={ 20 }/>
								<Skeleton { ...skeletonProps } width={ getPlaceholderWidth( 0.7 ) } height={ 20 }/>
								<Skeleton { ...skeletonProps } width={ 142 } height={ 100 }/>
								<Skeleton { ...skeletonProps } width={ 218 } height={ 20 }/>
							</Stack>
						</Box>
					</Stack>
				</Box>
			</BrowserFrame>
		</div>
	);
}

function EditorPreviewDescription() {
	return (
		<Stack
			sx={ {
				display: 'flex',
				flexDirection: 'column',
				gap: '10px',
				padding: '10px 0',
				maxWidth: getPlaceholderWidth( 0.5 ),
				textAlign: 'center',
				marginLeft: 'auto',
				marginRight: 'auto'
			} }
		>
			<Typography fontSize={ '40px' } lineHeight={ '46px' }>
				👈
			</Typography>
			<Typography fontWeight={ 700 } fontSize={ '24px' } lineHeight={ '32px' } color={ 'text.primary' }>
				Choose a preset on the left to start building your CKEditor
			</Typography>
			<Typography fontWeight={ 400 } fontSize={ '16px' } lineHeight={ '24px' } color={ 'text.secondary' }>
				Each preset has a dedicated editor type and selected initial set of features.
			</Typography>
		</Stack>
	);
}

function getPlaceholderWidth( percent: number ) {
	return `calc(var(--editor-preview-placeholder-width) * ${ percent })`;
}
